import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { ClickableUser } from "../contexts/UserSheetContext";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  ClipboardList, Loader2, CheckCircle, Clock, Users, Search,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import UserFicheSheet from "../components/users/UserFicheSheet";

// ── Helpers ───────────────────────────────────────────────────────────────────

const getInitials = (fn, ln) => `${fn?.[0] || ""}${ln?.[0] || ""}`.toUpperCase();

const ProgressBar = ({ completed, total }) => {
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-gray-200 dark:bg-[#1A3050] rounded-full h-1.5">
        <div className="bg-[#1A4D2E] dark:bg-[#4CAF70] h-1.5 rounded-full transition-all"
          style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-500 dark:text-[#7EA8CE] shrink-0">{completed}/{total}</span>
    </div>
  );
};

// ── OnboardingPage ─────────────────────────────────────────────────────────────

export default function OnboardingPage() {
  const { user } = useAuth();
  const [onboardings, setOnboardings] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sheetUserId, setSheetUserId] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const usersMap = useMemo(() =>
    Object.fromEntries(allUsers.map(u => [u.id, u])),
    [allUsers]
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [obRes, usersRes] = await Promise.all([
        axios.get(`${API}/onboarding`),
        axios.get(`${API}/users`),
      ]);
      setOnboardings(obRes.data);
      setAllUsers(usersRes.data);
    } catch (e) {
      toast.error("Erreur chargement des données");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openSheet = (userId) => {
    setSheetUserId(userId);
    setSheetOpen(true);
  };

  const enriched = useMemo(() => onboardings.map(ob => {
    const u = usersMap[ob.user_id];
    const manager = u?.manager_id ? usersMap[u.manager_id] : null;
    const completed = ob.steps.filter(s => s.status === "completed").length;
    const total = ob.steps.length;
    return { ...ob, user: u, manager, completed, total };
  }), [onboardings, usersMap]);

  const filtered = useMemo(() => {
    if (!searchQuery) return enriched;
    const q = searchQuery.toLowerCase();
    return enriched.filter(ob => {
      const name = ob.user ? `${ob.user.first_name} ${ob.user.last_name}`.toLowerCase() : "";
      const mgrName = ob.manager ? `${ob.manager.first_name} ${ob.manager.last_name}`.toLowerCase() : "";
      return name.includes(q) || mgrName.includes(q);
    });
  }, [enriched, searchQuery]);

  // KPI
  const inProgress = onboardings.filter(o => o.status === "in_progress").length;
  const completedCount = onboardings.filter(o => o.status === "completed").length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold dark:text-white">Onboarding</h1>
          <p className="text-sm text-gray-500 dark:text-[#7EA8CE] mt-0.5">
            Suivi de l'intégration des collaborateurs
          </p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="border dark:border-[#1A3050] rounded-xl p-4 bg-white dark:bg-[#0B1628]">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-amber-500" />
            <span className="text-xs text-gray-500 dark:text-[#7EA8CE]">En cours</span>
          </div>
          <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{inProgress}</p>
        </div>
        <div className="border dark:border-[#1A3050] rounded-xl p-4 bg-white dark:bg-[#0B1628]">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
            <span className="text-xs text-gray-500 dark:text-[#7EA8CE]">Terminés</span>
          </div>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">{completedCount}</p>
        </div>
        <div className="border dark:border-[#1A3050] rounded-xl p-4 bg-white dark:bg-[#0B1628]">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-[#1A4D2E] dark:text-[#4CAF70]" />
            <span className="text-xs text-gray-500 dark:text-[#7EA8CE]">Total</span>
          </div>
          <p className="text-2xl font-bold text-[#1A4D2E] dark:text-[#4CAF70]">{onboardings.length}</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-[#3D607E]" />
        <Input placeholder="Rechercher un collaborateur ou manager..."
          value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 sm:max-w-sm dark:bg-[#0B1628] dark:border-[#1A3050] dark:text-white dark:placeholder-[#3D607E]" />
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border dark:border-[#1A3050] rounded-xl bg-white dark:bg-[#0B1628]">
          <ClipboardList className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-[#3D607E]" />
          <p className="text-gray-500 dark:text-[#7EA8CE]">Aucun onboarding trouvé</p>
        </div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="sm:hidden space-y-3">
            {filtered.map((ob) => (
              <div key={ob.id} onClick={() => openSheet(ob.user_id)}
                className="border dark:border-[#1A3050] rounded-xl p-4 bg-white dark:bg-[#0B1628] cursor-pointer hover:bg-gray-50 dark:hover:bg-[#112240]/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Avatar className="w-10 h-10 shrink-0">
                    <AvatarImage src={ob.user?.avatar_url} />
                    <AvatarFallback className="text-sm bg-[#E8F5E9] dark:bg-[#1A3A6A] text-[#1A4D2E] dark:text-[#4B8BF4] font-semibold">
                      {ob.user ? getInitials(ob.user.first_name, ob.user.last_name) : "?"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm dark:text-[#D6E4F5] truncate">
                      {ob.user ? `${ob.user.first_name} ${ob.user.last_name}` : ob.user_id}
                    </p>
                    {ob.manager && (
                      <p className="text-xs text-gray-400 dark:text-[#3D607E]">
                        Manager : <ClickableUser userId={ob.manager.id}>{ob.manager.first_name} {ob.manager.last_name}</ClickableUser>
                      </p>
                    )}
                  </div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${
                    ob.status === "completed"
                      ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                      : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                  }`}>
                    {ob.status === "completed" ? "Terminé" : "En cours"}
                  </span>
                </div>
                <ProgressBar completed={ob.completed} total={ob.total} />
                <p className="text-xs text-gray-400 dark:text-[#3D607E] mt-2">
                  Démarré le {format(new Date(ob.started_at), "dd/MM/yyyy", { locale: fr })}
                </p>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block border dark:border-[#1A3050] rounded-xl overflow-hidden bg-white dark:bg-[#0B1628]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b dark:border-[#1A3050] text-left bg-gray-50 dark:bg-[#112240]/60">
                  <th className="py-3 px-4 font-medium text-gray-500 dark:text-[#7EA8CE]">Collaborateur</th>
                  <th className="py-3 px-4 font-medium text-gray-500 dark:text-[#7EA8CE]">Manager</th>
                  <th className="py-3 px-4 font-medium text-gray-500 dark:text-[#7EA8CE]">Démarré le</th>
                  <th className="py-3 px-4 font-medium text-gray-500 dark:text-[#7EA8CE]">Étapes</th>
                  <th className="py-3 px-4 font-medium text-gray-500 dark:text-[#7EA8CE]">Statut</th>
                  <th className="py-3 px-4 font-medium text-gray-500 dark:text-[#7EA8CE]">Progression</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-[#1A3050]">
                {filtered.map((ob) => (
                  <tr key={ob.id} onClick={() => openSheet(ob.user_id)}
                    className="hover:bg-gray-50 dark:hover:bg-[#112240]/30 cursor-pointer transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Avatar className="w-7 h-7 shrink-0">
                          <AvatarImage src={ob.user?.avatar_url} />
                          <AvatarFallback className="text-xs bg-[#E8F5E9] dark:bg-[#1A3A6A] text-[#1A4D2E] dark:text-[#4B8BF4] font-semibold">
                            {ob.user ? getInitials(ob.user.first_name, ob.user.last_name) : "?"}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium dark:text-[#D6E4F5]">
                            {ob.user ? `${ob.user.first_name} ${ob.user.last_name}` : ob.user_id}
                          </p>
                          {ob.user?.department && (
                            <p className="text-xs text-gray-400 dark:text-[#3D607E]">{ob.user.department}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-500 dark:text-[#7EA8CE]">
                      {ob.manager ? <ClickableUser userId={ob.manager.id}>{ob.manager.first_name} {ob.manager.last_name}</ClickableUser> : "—"}
                    </td>
                    <td className="py-3 px-4 text-gray-500 dark:text-[#7EA8CE]">
                      {format(new Date(ob.started_at), "dd/MM/yyyy", { locale: fr })}
                    </td>
                    <td className="py-3 px-4 text-gray-500 dark:text-[#7EA8CE]">
                      {ob.completed}/{ob.total}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        ob.status === "completed"
                          ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                          : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                      }`}>
                        {ob.status === "completed" ? "Terminé" : "En cours"}
                      </span>
                    </td>
                    <td className="py-3 px-4 w-36">
                      <ProgressBar completed={ob.completed} total={ob.total} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* UserFicheSheet — onglet onboarding */}
      <UserFicheSheet
        userId={sheetUserId}
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        allUsers={allUsers}
        onRefresh={fetchData}
        onOpenUser={(uid) => { setSheetUserId(uid); }}
      />
    </div>
  );
}
